import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { ApiService } from '../services/apiService';
import { CardData, CardState } from '../types/cardTypes';
import BiometricSetup from '../components/BiometricSetup';
import { PageShell } from '../components/layout/PageShell';
import { StatusPageLayout } from '../components/layout/StatusPageLayout';
import { StatusIcon } from '../components/layout/StatusIcon';
import './ThankYou.css';

const ThankYou: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [cardData, setCardData] = useState<CardData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showBiometricSetup, setShowBiometricSetup] = useState(false);
  const [biometricSetupComplete, setBiometricSetupComplete] = useState(false);

  const cardId = searchParams.get('cardId');

  const fetchCardData = useCallback(async () => {
    try {
      setIsLoading(true);
      const data = await ApiService.getCardByCardId(cardId!);
      setCardData(data);
    } catch (err) {
      console.error('Error fetching card data:', err);
      setError('Failed to load card details');
    } finally {
      setIsLoading(false);
    }
  }, [cardId]);

  useEffect(() => {
    if (cardId) fetchCardData();
  }, [cardId, fetchCardData]);

  const handleCheckBalance = () => {
    if (cardData?.state === CardState.GIFT_CARD) {
      navigate(`/new-card?cardId=${cardId}&giftcard=true`);
    } else {
      navigate(`/balance-display?cardId=${cardId}`);
    }
  };

  if (isLoading) {
    return (
      <StatusPageLayout variant="loading" title="Loading" body="Loading card details…" />
    );
  }

  if (error || !cardData) {
    return (
      <StatusPageLayout variant="error" title="Error" body={error || 'Card not found'} />
    );
  }

  if (showBiometricSetup) {
    return (
      <PageShell showFooter={false}>
        <BiometricSetup
          cardId={cardId!}
          onSuccess={() => {
            setBiometricSetupComplete(true);
            setShowBiometricSetup(false);
          }}
          onSkip={() => setShowBiometricSetup(false)}
        />
      </PageShell>
    );
  }

  return (
    <PageShell showFooter={false} wide>
      <div className="ev-shell-card thank-you-card ev-animate-in">
        <StatusIcon variant="success" />
        <h1 className="ev-status-title">Thank you for your purchase</h1>

        <div className="thank-you-details">
          <div className="thank-you-row">
            <span>Card ID</span>
            <strong>{cardData.cardId}</strong>
          </div>
          <div className="thank-you-row">
            <span>Amount</span>
            <strong>${cardData.originalAmount} AUD</strong>
          </div>
        </div>

        {cardData.state === CardState.GIFT_CARD ? (
          <>
            <h3 className="thank-you-subhead">Gift card purchase complete</h3>
            <p className="ev-status-body">
              To see what your gift card recipient will see, tap the button below.
            </p>
            <button type="button" className="ev-btn-primary" onClick={handleCheckBalance}>
              Gift me
            </button>
          </>
        ) : (
          <>
            <h3 className="thank-you-subhead">Purchase complete</h3>
            <p className="ev-status-body">
              To check your balance, scan your card again or use the button below.
            </p>
            <button type="button" className="ev-btn-primary" onClick={handleCheckBalance}>
              Check balance
            </button>
          </>
        )}

        {!biometricSetupComplete && cardData.state === CardState.GIFT_CARD && (
          <div className="thank-you-biometric">
            <h3 className="thank-you-subhead">Enhance your security</h3>
            <p className="ev-status-body">
              Add biometric authentication for quick, secure access to all your cards.
            </p>
            <button type="button" className="ev-btn-secondary" onClick={() => setShowBiometricSetup(true)}>
              Setup biometric authentication
            </button>
          </div>
        )}
      </div>
    </PageShell>
  );
};

export default ThankYou;
